import React from 'react'
import { useNewTranslation } from 'hooks'
import { ToggleContainer, ToggleButtonSize, SwitchButton, Container } from './style'
import Icon from 'components/Icon/Icon'
import Text, { TextScale } from 'particles/Text'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import CommentContainer from 'components/Shared/Comment'
import { useField, useFormikContext } from 'formik'

const sizeMap: Record<ToggleButtonSize, { iconSize: string; textScale: TextScale }> = {
    m: {
        iconSize: '10px',
        textScale: 'bodyM400',
    },
    s: {
        iconSize: '8px',
        textScale: 'bodyS400',
    },
}

interface ToggleButtonProps {
    className?: string
    isDisabled?: boolean
    handleChange?: () => void
    size?: ToggleButtonSize
    label?: string
    subtext?: string
    isReadOnly?: boolean
    isChecked?: boolean
    isRequired?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    identifier?: string
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
    dataTestId?: string
}

const Toggle: React.FC<ToggleButtonProps> = ({
    className,
    isChecked = false,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    isCommentable = false,
    isCommented = false,
    handleChange,
    size = 'm',
    label = '',
    subtext,
    onLabelClick,
    identifier,
    labelSize,
    link,
    tooltip,
    dataTestId,
}) => {
    const [t] = useNewTranslation()

    const handleSwitch = () => {
        if (!isDisabled && handleChange) {
            handleChange()
        }
    }

    return (
        <Container size={size}>
            {!isReadOnly ? (
                <ToggleContainer
                    size={size}
                    className={`toggle-switcher ${className} ${isChecked ? 'on' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={handleSwitch}
                    data-testid={dataTestId}
                >
                    <SwitchButton size={size} className={`switch-button ${isDisabled ? 'disabled' : ''}`} />
                    <Icon.ToggleOnIcon
                        className={`toggle check ${isChecked ? '' : 'hidden'}`}
                        height={sizeMap[size].iconSize}
                        width={sizeMap[size].iconSize}
                    />
                    <Icon.ToggleOffIcon
                        className={`toggle unchecked ${isChecked ? 'hidden' : ''}`}
                        height={sizeMap[size].iconSize}
                        width={sizeMap[size].iconSize}
                    />
                </ToggleContainer>
            ) : (
                <Text
                    data-TestId={`${dataTestId}-readOnly`}
                    scale={sizeMap[size].textScale}
                    color="disable"
                    as="div"
                    className="readonly-text"
                >
                    {isChecked ? t('button.on') : t('button.toggle.off')}
                </Text>
            )}
            {(label || subtext) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={label}
                        subtext={subtext}
                        isRequired={isRequired}
                        size={labelSize ? labelSize : size}
                        isCommentable={isCommentable}
                        isCommented={isCommented}
                        onClick={onLabelClick}
                        link={link}
                        tooltip={tooltip}
                    />
                </CommentContainer>
            )}
        </Container>
    )
}

export default Toggle

interface FormikNumberInputProps extends ToggleButtonProps {
    name: string
}

export const FormikToggle: React.FC<FormikNumberInputProps> = ({ name, ...props }) => {
    const [field, meta] = useField(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const handleChange = () => {
        setFieldValue(name, !field.value)
        if (!meta.touched) setFieldTouched(field.name, true, false)
    }

    return <Toggle {...props} handleChange={handleChange} isChecked={field.value} />
}
